import { Injectable } from '@angular/core';
import { VideoServerService } from './videoserver.service';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, tap, toArray } from 'rxjs/operators';
import { CameraVisit } from 'app/models/camerasupervision.model';
import { Action, IRemovedScheduledVisit, IScheduledVisitChangeRecord } from 'app/models/activity-log.model';
import moment from "moment-timezone";

export interface IScheduledVisitLog {
  cameraGroupId: string;
  cameraGroupName: string;
  action: Action;
  changeRecord: IScheduledVisitChangeRecord;
}

export function getVisitDurationTimes(visit: CameraVisit): string {
  if (visit.isNightowl) {
    const hasLocalizedTimes = visit.time?.timeZone != null && visit.time.timeZone.toUpperCase() !== 'UTC';

    if (hasLocalizedTimes) {
      const startHour = String(visit.time.hour).padStart(2, '0');
      const startMinute = String(visit.time.minute).padStart(2, '0');

      const startTotalMinutes = visit.time.hour * 60 + visit.time.minute;
      const endTotalMinutes = (startTotalMinutes + visit.duration) % (24 * 60);
      const endHour = String(Math.floor(endTotalMinutes / 60)).padStart(2, '0');
      const endMinute = String(endTotalMinutes % 60).padStart(2, '0');

      return `${startHour}:${startMinute} - ${endHour}:${endMinute}`;
    }

    // Fallback for legacy data (timezone missing or UTC in time object).
    const startDate = new Date(visit.startDate);
    const startHour = String(startDate.getHours()).padStart(2, '0');
    const startMinute = String(startDate.getMinutes()).padStart(2, '0');

    const endDate = new Date(startDate.getTime() + visit.duration * 60000);
    const endHour = String(endDate.getHours()).padStart(2, '0');
    const endMinute = String(endDate.getMinutes()).padStart(2, '0');

    return `${startHour}:${startMinute} - ${endHour}:${endMinute}`;
  } else {
    let startTime = moment(visit.startDate).format("kk:mm");
    let endTime = moment(visit.startDate).add(visit.duration, "minutes").format("kk:mm");

    startTime = formatTime(startTime);
    endTime = formatTime(endTime);
    return `${startTime} - ${endTime}`;
  }
}

export function formatTime(timeString: string): string {
  // Convert 24:xx to 00:xx
  return timeString.startsWith("24") ? "00" + timeString.substring(2) : timeString;
}

@Injectable({
  providedIn: 'root'
})
export class CameraScheduleService {

  constructor(
    private videoServer: VideoServerService,
    private cookies: CookieService
  ) { }

  /**
   * Delete all visits for a camera group
   * @param cameraGroupId Camera group id to delete all visits for
   */
  deleteCameraGroupVisits(cameraGroupId: string): Observable<IRemovedScheduledVisit[]> {
    return this.getAllCameraGroupVisits(cameraGroupId).pipe(
      mergeMap((visitsToDelete) => from(visitsToDelete)), // Convert retrieved visits to an observable stream
      // Delete visits sequentially and then log the deletion
      concatMap((visit) => this.videoServer.deleteVisit(cameraGroupId, visit.id).pipe(
        map(() => visit), // Pass visit forward if deleted successfully
        catchError(() => of(null)) // Ignore the deletion error and pass null
      )),
      filter((visit) => visit !== null), // Remove failed deletions (null) from the list
      toArray(), // Wait for all deletions to complete and collect them into an array
      map((deletedVisits) => this.buildScheduledVisitLogs(deletedVisits)), // Build logs from the deleted visits
    );
  }

  /**
   * Get all visits for a camera group
   * @param cameraGroupId Camera group id to get all visits for
   * @returns List containing all visits for the camera group
   */
  getAllCameraGroupVisits(cameraGroupId: string): Observable<CameraVisit[]> {
    // Get all camera visits and filter by cameraGroupId
    return this.getAllCameraVisits().pipe(
      map((allVisits) => allVisits.filter((visit: any) => visit.cameraGroupId === cameraGroupId))
    );
  }

  /**
   * Get all camera visits for the company
   * @returns List containing all camera visits
   */
  getAllCameraVisits(): Observable<CameraVisit[]> {
    return this.checkToken().pipe(
      concatMap(() =>
        forkJoin({
          futureVisits: this.videoServer.getAllCameraVisits("10079"),
          activeVisits: this.videoServer.getActiveCameraVisits()
        }).pipe(
          map(({ futureVisits, activeVisits }) => this.mergeVisits(futureVisits, activeVisits))
        )
      )
    );
  }

  private checkToken(): Observable<void> {
    if (!this.cookies.get("videoserver-session-token")) {
      return this.videoServer.login().pipe(
        tap((response) => {
          const token = response.token;
          this.cookies.set("videoserver-session-token", `Bearer ${token}`, undefined, "/", undefined, false, "Lax");
        }),
        map(() => undefined)
      );
    }
    return of(undefined); // Need to emit something to continue the pipe
  }

  private mergeVisits(futureVisits: CameraVisit[], activeVisits: any[]): CameraVisit[] {
    const allVisits = futureVisits ?? [];

    activeVisits?.forEach((visit: any) => {
      const visitItem: CameraVisit = visit.scheduleItem;
      visitItem.activeVisit = true;
      visitItem.visitType = visit.visitType;
      allVisits.push(visitItem);
    });

    return allVisits;
  }

  private buildScheduledVisitLogs(visits: CameraVisit[]): IRemovedScheduledVisit[] {
    const logs = [];

    for (const visit of visits) {
      const log: IRemovedScheduledVisit = {
        startTime: visit.startDate,
        durationMinutes: visit.duration,
        extendedVisitTimeMinutes: visit.gracePeriod
      };
      logs.push(log);
    }
    return logs;
  }
}
